import { ReadonlyArray } from "@morphism/fp";
import React from "react";
import styled from "styled-components";

import * as Color from "~/Color";
import { DVIR, DVIRs } from "~/DVIR";
import { Order } from "~/Order";

import { PieChart } from "./PieChart";

export type Statistics = {
  readonly count: number;
  readonly defects: {
    readonly resolved: number;
    readonly unresolved: number;
    readonly none: number;
  };
};

export function Statistics({
  loading = false,
  dvirSummaries,
  logsWithoutDVIRs,
  logsWithDVIRs,
}: {
  readonly dvirSummaries: readonly DVIRs.Summary[];
  readonly logsWithoutDVIRs: number;
  readonly logsWithDVIRs: number;
  readonly loading: boolean;
}) {
  return pipe(
    dvirSummaries,
    Statistics.fromSummaries,
    (statistics) => ({
      counts: statistics,
      percentages: Statistics.asPercentages(statistics),
    }),
    ({ counts, percentages }) => (
      <Statistics.Container>
        {loading ? (
          <p>Loading statistics...</p>
        ) : (
          <>
            <Statistics.Card>
              <PieChart
                title="DVIR defects"
                data={[
                  {
                    name: "Resolved defects",
                    count: counts.defects.resolved,
                    percentage: percentages.defects.resolved,
                    color: "#00CB86",
                  },
                  {
                    name: "Unresolved defects",
                    count: counts.defects.unresolved,
                    percentage: percentages.defects.unresolved,
                    color: "#DA344D",
                  },
                  {
                    name: "No defects",
                    count: counts.defects.none,
                    percentage: percentages.defects.none,
                    color: "#929DA9",
                  },
                ]}
                width={116}
                height={116}
                innerRadius={38}
                outerRadius={58}
              />
            </Statistics.Card>
            <Statistics.Card>
              <PieChart
                title="DVIRs and logs"
                data={[
                  {
                    name: "Logs with DVIRs",
                    color: "#5E66B6",
                    count: logsWithDVIRs,
                    percentage:
                      logsWithDVIRs / (logsWithDVIRs + logsWithoutDVIRs),
                  },
                  {
                    name: "Logs without DVIRs",
                    color: "#929DA9",
                    count: logsWithoutDVIRs,
                    percentage:
                      logsWithoutDVIRs / (logsWithDVIRs + logsWithoutDVIRs),
                  },
                ]}
                width={116}
                height={116}
                innerRadius={38}
                outerRadius={58}
              />
            </Statistics.Card>
            <Statistics.Card>
              <div style={{ fontSize: 36 }}>{counts.count}</div>
              <div style={{ fontSize: 20 }}>Total DVIRs</div>
            </Statistics.Card>
          </>
        )}
      </Statistics.Container>
    )
  );
}

export namespace Statistics {
  export const empty = (): Statistics => ({
    count: 0,
    defects: {
      resolved: 0,
      unresolved: 0,
      none: 0,
    },
  });

  export const concat = (a: Statistics) => (b: Statistics): Statistics => ({
    count: a.count + b.count,
    defects: {
      resolved: a.defects.resolved + b.defects.resolved,
      unresolved: a.defects.unresolved + b.defects.unresolved,
      none: a.defects.none + b.defects.none,
    },
  });

  export const order = (): Order<Statistics> =>
    Order.fromCompare((a, b) => {
      const count = Order.number().compare(a.count, b.count);
      if (count !== Order.equal()) return count;

      const unresolved = Order.number().compare(
        a.defects.unresolved,
        b.defects.unresolved
      );

      if (unresolved !== Order.equal()) return unresolved;

      const resolved = Order.number().compare(
        a.defects.resolved,
        b.defects.resolved
      );

      return resolved !== Order.equal()
        ? resolved
        : Order.number().compare(a.defects.none, b.defects.none);
    });

  export const fromDVIR = (dvir: DVIR): Statistics =>
    pipe(
      dvir.defects,
      ReadonlyArray.reduce(empty(), (previous, { defect }) => ({
        ...previous,
        defects: {
          ...previous.defects,
          ...(defect.resolution
            ? { resolved: previous.defects.resolved + 1 }
            : { unresolved: previous.defects.unresolved + 1 }),
        },
      })),
      ({ defects }) => ({
        count: 1,
        defects: {
          ...defects,
          none: defects.resolved || defects.unresolved ? 0 : 1,
        },
      })
    );

  export const fromDVIRs = (dvirs: DVIRs): Statistics =>
    pipe(
      dvirs,
      ReadonlyArray.reduce(empty(), (previous, dvir) =>
        pipe(fromDVIR(dvir), ({ count, defects }) => ({
          count: previous.count + count,
          defects: {
            resolved: previous.defects.resolved + defects.resolved,
            unresolved: previous.defects.unresolved + defects.unresolved,
            none:
              defects.resolved || defects.unresolved
                ? previous.defects.none
                : previous.defects.none + 1,
          },
        }))
      )
    );

  export const fromDvirSummary = (
    dvirSummary: DVIRs.Summary.DriverDvirSummary
  ): Statistics =>
    pipe(dvirSummary ?? empty(), (summary) => ({
      count: 1,
      defects: {
        resolved: summary.resolvedDefects,
        unresolved: summary.unresolvedDefects,
        none: summary.resolvedDefects || summary.unresolvedDefects ? 0 : 1,
      },
    }));

  export const fromDvirSummaries = (
    dvirSummary: readonly DVIRs.Summary.DriverDvirSummary[]
  ): Statistics =>
    pipe(
      dvirSummary,
      ReadonlyArray.reduce<DVIRs.Summary.DriverDvirSummary, Statistics>(
        empty(),
        (previous, current) => ({
          ...previous,
          defects: {
            ...previous.defects,
            ...{
              resolved: previous.defects.resolved + current.resolvedDefects,
              unresolved:
                previous.defects.unresolved + current.unresolvedDefects,
            },
          },
        })
      ),
      ({ defects }) => ({
        count: 1,
        defects: {
          ...defects,
          none: defects.resolved || defects.unresolved ? 0 : 1,
        },
      })
    );

  export const fromSummary = (summary: DVIRs.Summary): Statistics => ({
    count: summary.totalDVIRs,
    defects: {
      resolved: summary.resolvedDefects,
      unresolved: summary.unresolvedDefects,
      none: summary.resolvedDefects || summary.unresolvedDefects ? 0 : 1,
    },
  });

  export const fromSummaries = (
    summaries: readonly DVIRs.Summary[]
  ): Statistics =>
    pipe(
      summaries,
      ReadonlyArray.reduce(empty(), (previous, summary) =>
        pipe(fromSummary(summary), ({ count, defects }) => ({
          count: previous.count + count,
          defects: {
            resolved: previous.defects.resolved + defects.resolved,
            unresolved: previous.defects.unresolved + defects.unresolved,
            none:
              defects.resolved || defects.unresolved
                ? previous.defects.none
                : previous.defects.none + 1,
          },
        }))
      )
    );

  export const asPercentages = (statistics: Statistics): Statistics =>
    pipe(
      statistics.defects.resolved +
        statistics.defects.unresolved +
        statistics.defects.none,
      (total) => ({
        ...statistics,
        defects: {
          resolved: statistics.defects.resolved / total,
          unresolved: statistics.defects.unresolved / total,
          none: statistics.defects.none / total,
        },
      })
    );

  export const Container = styled.div`
    display: flex;
    justify-content: space-evenly;
    align-items: center;
    height: 200px;
    border-bottom: 1px solid ${Color.veryWhiteGrey()};
  `;

  export const Card = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
  `;
}
